// SUB-OFFICE 7 — the building, constructed. Hand-authored layout DATA in,
// merged procedural GEOMETRY out. Five shape castes, flat shading, vertex
// colors (itemSize 4 — alpha carries the photocopier-banding flag), and as
// few draw calls as the material budget allows. Target: <=80 calls, <=60k tris.

import {
  BoxGeometry, BufferAttribute, BufferGeometry, CatmullRomCurve3, Color,
  CylinderGeometry, Group, Matrix3, Matrix4, Mesh, MeshBasicMaterial,
  MeshLambertMaterial, PlaneGeometry, TorusGeometry, TubeGeometry, Vector3,
  AdditiveBlending, DoubleSide, type Texture,
} from 'three';
import { PAL, shade, mix, roomShadow, roomWash } from '../palette';
import {
  WALLS, ROOMS, WINDOW_X, WINDOW_Z, DOORS, DESKS, BENCHES, DISPENSER,
  SPLITFLAP, TURNSTILE, PLAQUE, COPIER, LETTERHEAD_BOARD, PODIUMS,
  POSTER_SPOTS, DRAWERS, GALLERY_WALL, REGISTRY_PLAQUES, DECALS, TUBES,
  TUBE_RING_SPACING, VAULT, LAMPS, LAMP_Y, CHYRONS, LECTERN, FORM3_LECTERN,
  JUNCTION_FULL, JUNCTION_TAP, DISC_FULL, DISC_TEASER,
  WALL_T, type RoomDef,
} from '../world/layout';
import {
  makeFloorTexture, makeDecalAtlas, makePosterAtlas, makeGalleryAtlas,
  makePlaqueTexture, makeStencilTexture, makeLetterheadBoardTexture,
  makeFrostTexture, makeCeilingTexture, makeHardwareAtlas, type HardwareLabel,
} from './textures';
import { patchMaterial } from './scene';

// ---------------------------------------------------------------------------
// Batcher: absorbs geometries into one non-indexed soup with rgba colors
// ---------------------------------------------------------------------------

const _m4 = new Matrix4();
const _nm = new Matrix3();
const _v = new Vector3();
const _c = new Color();

class Batch {
  pos: number[] = [];
  nrm: number[] = [];
  col: number[] = [];

  absorb(geo: BufferGeometry, matrix: Matrix4, hex: number, band = 0): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    _nm.getNormalMatrix(matrix);
    _c.setHex(hex).convertSRGBToLinear();
    for (let i = 0; i < p.count; i++) {
      _v.fromBufferAttribute(p, i).applyMatrix4(matrix);
      this.pos.push(_v.x, _v.y, _v.z);
      _v.fromBufferAttribute(n, i).applyMatrix3(_nm).normalize();
      this.nrm.push(_v.x, _v.y, _v.z);
      this.col.push(_c.r, _c.g, _c.b, band);
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }

  box(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number, hex: number, band = 0, rotY = 0): void {
    _m4.makeRotationY(rotY).setPosition(cx, cy, cz);
    this.absorb(new BoxGeometry(sx, sy, sz), _m4.clone(), hex, band);
  }

  /**
   * The softness workhorse. A box whose vertical faces carry a baked
   * top-lit vertex gradient (top = hex, bottom darkened by `grad`). This turns
   * every flat slab into a graded surface — the single cheapest kill for
   * "harsh poly" flatness. Top/bottom caps stay flat.
   *
   * v3-2 (WARM MATERIAL PASS): two appeal levers ride this same gradient at
   * +0 cost. (1) `dark` = a room SHADOW HUE to darken the bottom TOWARD, so the
   * prop's contact shadow shares the room's temperature with the floor AO
   * (§4.2 lever 1) instead of muddy black; omit it and the legacy neutral-black
   * darken is used verbatim, so mechanisms/consequences keep their cold edges.
   * (2) `topLift` = a generous WARM rim: the very top edge (tv>0.85) mixes
   * toward the key light, the "soft daylight on a toy" highlight on hero
   * furniture. Both are pure vertex-color math — no new material/geometry.
   */
  boxG(
    cx: number, cy: number, cz: number, sx: number, sy: number, sz: number,
    hex: number, opts: { band?: number; rotY?: number; grad?: number; topLift?: number; dark?: number } = {}
  ): void {
    const band = opts.band ?? 0;
    const rotY = opts.rotY ?? 0;
    const grad = opts.grad ?? 0.12;
    const topLift = opts.topLift ?? 0; // extra WARM brighten at the very top edge
    const dark = opts.dark; // room shadow hue to darken toward; undefined = black
    const geo = new BoxGeometry(sx, sy, sz);
    const g = geo.toNonIndexed();
    const p = g.getAttribute('position');
    const n = g.getAttribute('normal');
    _m4.makeRotationY(rotY).setPosition(cx, cy, cz);
    _nm.getNormalMatrix(_m4);
    const half = sy / 2 || 1;
    for (let i = 0; i < p.count; i++) {
      const ly = p.getY(i);
      const tv = (ly + half) / (2 * half); // 0 bottom .. 1 top
      const down = grad * (1 - tv); // darken amount at this height
      const up = topLift * Math.max(0, tv - 0.85); // warm rim at the very top
      // v3-2: colored contact shadow (toward the room hue) when `dark` is given,
      // else the legacy neutral-black darken (mechanisms stay cold); then a warm
      // key-light rim on the topmost edge.
      let vhex = dark !== undefined ? mix(hex, dark, down) : shade(hex, -down);
      if (up > 0) vhex = mix(vhex, PAL.keyLight, up);
      _c.setHex(vhex).convertSRGBToLinear();
      _v.fromBufferAttribute(p, i).applyMatrix4(_m4);
      this.pos.push(_v.x, _v.y, _v.z);
      _v.fromBufferAttribute(n, i).applyMatrix3(_nm).normalize();
      this.nrm.push(_v.x, _v.y, _v.z);
      this.col.push(_c.r, _c.g, _c.b, band);
    }
    g.dispose();
    geo.dispose();
  }

  /**
   * CHAMFER LAW — a proud top-edge lip: a thin frame, 2cm proud on each side,
   * shaded up (+lift) so the key light catches it as a bright rim. Reads as a
   * die-cut bevel on desks, counters, benches, cabinets. Four thin bars, so it
   * survives the flat-shade language and adds a horizontal highlight line where
   * two big faces used to meet in one hard corner.
   */
  chamfer(cx: number, yTop: number, cz: number, sx: number, sz: number, hex: number, rotY = 0, proud = 0.02, t = 0.025): void {
    const lift = shade(hex, 0.12);
    const ex = sx + proud * 2, ez = sz + proud * 2;
    // long bars along x (front/back edges), short bars along z (left/right)
    this.box(cx, yTop, cz - sz / 2 - proud / 2, ex, t, proud, lift, 0, rotY);
    this.box(cx, yTop, cz + sz / 2 + proud / 2, ex, t, proud, lift, 0, rotY);
    this.box(cx - sx / 2 - proud / 2, yTop, cz, proud, t, ez, lift, 0, rotY);
    this.box(cx + sx / 2 + proud / 2, yTop, cz, proud, t, ez, lift, 0, rotY);
  }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nrm), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 4));
    return g;
  }

  get tris(): number {
    return this.pos.length / 9;
  }
}

/** UV-quad batch for atlas surfaces (posters, gallery, decals). */
class QuadBatch {
  pos: number[] = [];
  uv: number[] = [];

  /** rotDeg: 0 faces +z; 90 faces +x; -90 faces -x; 180 faces -z. */
  wallQuad(cx: number, cy: number, cz: number, w: number, h: number, rotDeg: number, r: { u0: number; v0: number; u1: number; v1: number }): void {
    const a = (rotDeg * Math.PI) / 180;
    const dx = Math.cos(a) * (w / 2);
    const dz = -Math.sin(a) * (w / 2);
    const x0 = cx - dx, z0 = cz - dz, x1 = cx + dx, z1 = cz + dz;
    const y0 = cy - h / 2, y1 = cy + h / 2;
    this.pos.push(x0, y0, z0, x1, y0, z1, x1, y1, z1, x0, y0, z0, x1, y1, z1, x0, y1, z0);
    this.uv.push(r.u0, r.v0, r.u1, r.v0, r.u1, r.v1, r.u0, r.v0, r.u1, r.v1, r.u0, r.v1);
  }

  floorQuad(cx: number, cz: number, sx: number, sz: number, rotDeg: number, r: { u0: number; v0: number; u1: number; v1: number }, y = 0.02): void {
    const a = (rotDeg * Math.PI) / 180;
    const c = Math.cos(a), s = Math.sin(a);
    const corner = (lx: number, lz: number): [number, number] => [cx + lx * c - lz * s, cz + lx * s + lz * c];
    const [ax, az] = corner(-sx / 2, sz / 2);
    const [bx, bz] = corner(sx / 2, sz / 2);
    const [cx2, cz2] = corner(sx / 2, -sz / 2);
    const [dx2, dz2] = corner(-sx / 2, -sz / 2);
    this.pos.push(ax, y, az, bx, y, bz, cx2, y, cz2, ax, y, az, cx2, y, cz2, dx2, y, dz2);
    this.uv.push(r.u0, r.v0, r.u1, r.v0, r.u1, r.v1, r.u0, r.v0, r.u1, r.v1, r.u0, r.v1);
  }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    g.computeVertexNormals();
    return g;
  }
}

// ---------------------------------------------------------------------------
// GREEBLE VOCABULARY — the Machine's one fixture kit (modeling_language).
// Every hardware detail in the building draws from these primitives so the
// whole Bureau reads as a single manufacturer. Nothing else. No fantasy greeble.
// ---------------------------------------------------------------------------

/** hex bolt: a stubby 6-sided cylinder proud of a face. dir = the face normal. */
function hexBolt(b: Batch, x: number, y: number, z: number, r: number, len: number, hex: number = PAL.brass, dir: 'x' | '-x' | 'z' | '-z' | 'y' = 'z'): void {
  const geo = new CylinderGeometry(r, r, len, 6);
  if (dir === 'z' || dir === '-z') _m4.makeRotationX(Math.PI / 2);
  else if (dir === 'x' || dir === '-x') _m4.makeRotationZ(Math.PI / 2);
  else _m4.identity();
  _m4.setPosition(x, y, z);
  b.absorb(geo, _m4.clone(), hex);
}

/** inspection plate: a thin proud panel with two bolt dots. faces +z or +x. */
function inspectionPlate(b: Batch, cx: number, cy: number, cz: number, w: number, h: number, hex: number, dir: 'z' | 'x' = 'z', depth = 0.03): void {
  const plate = shade(hex, 0.2);
  if (dir === 'z') {
    b.boxG(cx, cy, cz + depth / 2, w, h, depth, plate, { grad: 0.14 });
    hexBolt(b, cx - w / 2 + 0.06, cy + h / 2 - 0.06, cz + depth + 0.01, 0.02, 0.03, PAL.brass, 'z');
    hexBolt(b, cx + w / 2 - 0.06, cy - h / 2 + 0.06, cz + depth + 0.01, 0.02, 0.03, PAL.brass, 'z');
  } else {
    b.boxG(cx + depth / 2, cy, cz, depth, h, w, plate, { grad: 0.14 });
    hexBolt(b, cx + depth + 0.01, cy + h / 2 - 0.06, cz - w / 2 + 0.06, 0.02, 0.03, PAL.brass, 'x');
    hexBolt(b, cx + depth + 0.01, cy - h / 2 + 0.06, cz + w / 2 - 0.06, 0.02, 0.03, PAL.brass, 'x');
  }
}

/** louver vent: N thin horizontal slats recessed in a dark reveal. faces +z/+x. */
function louver(b: Batch, cx: number, cy: number, cz: number, w: number, h: number, count: number, dir: 'z' | 'x' = 'z'): void {
  const step = h / count;
  const dark = shade(PAL.carbon, 0.02);
  const slat = shade(PAL.carbon, 0.28);
  for (let i = 0; i < count; i++) {
    const yy = cy - h / 2 + step * (i + 0.5);
    if (dir === 'z') {
      b.box(cx, yy, cz + 0.005, w, step * 0.35, 0.01, dark);
      b.box(cx, yy + step * 0.18, cz + 0.02, w, step * 0.28, 0.02, slat);
    } else {
      b.box(cx + 0.005, yy, cz, 0.01, step * 0.35, w, dark);
      b.box(cx + 0.02, yy + step * 0.18, cz, 0.02, step * 0.28, w, slat);
    }
  }
}

// ---------------------------------------------------------------------------

export interface BuiltWorld {
  group: Group;
  tris: number;
  buildMs: number;
  /** dynamic handles for entities */
  w3Tube: Mesh; // the flickering fixture over Window 3
  w3Glow: Mesh;
  dept6Glow: Mesh; // the only green in the game
  pilotLamp: Mesh; // dispenser STAMP RED pulse
  beacon: Mesh; // vault lighthouse cone
  vaultMouthGlow: Mesh; // amber pool at the mouth; warms one step in adjudication
  copierPilots: Mesh[]; // 3 control-strip lamps (amber/amber/red), lit on REISSUE
  copierScan: Mesh; // the lid scan line; sweeps during REISSUE
  turnstileArms: Group;
  frostMat: MeshBasicMaterial;
  windowAnchors: { x: number; z: number }[];
  chyronGeo: BufferGeometry; // props.ts skins this with the live chyron texture
  /** the FP sky: tile grid + troffers + vents; hidden in the long-lens view */
  ceiling: Group;
  /** paperwork torsos behind the frost, one per window (windows.ts shuffles them) */
  clerkTorsos: Mesh[];
  /** lamp-pool glow cards; boot deepens them in the FP vantage */
  glowMat: MeshBasicMaterial;
}

export function buildWorld(): BuiltWorld {
  const t0 = performance.now();
  const group = new Group();
  const staticB = new Batch(); // lit, lambert, banded
  const unlitB = new Batch(); // emissive boxes (fluorescents, housings)
  const glowB = new QuadBatch(); // additive glow cards
  const hwAtlas = makeHardwareAtlas(); // one texture for every new READ-3 label
  const hwB = new QuadBatch(); // all hero-prop / station labels, one draw call

  // ---- architecture: walls with 3cm top chamfer ---------------------------
  // room-mood vertex pass: walls inherit the deepest dim of any room they
  // border, finally moving RoomDef.dim off the floor and onto the architecture
  // v3 (§4.3): a dim room dims its walls toward its own SHADOW HUE, not neutral
  // black — the deepest-dim room bordering a wall lends its temperature. Returns
  // that dim plus the matching shadow hue (defaults to Recess's neutral grey).
  const roomMoodAt = (x: number, z: number): { dim: number; shadow: number } => {
    let dim = 0;
    let shadow = 0x22211e;
    for (const r of ROOMS) {
      if (x >= r.x0 - 0.35 && x <= r.x1 + 0.35 && z >= r.z0 - 0.35 && z <= r.z1 + 0.35) {
        if (r.dim >= dim) {
          dim = r.dim;
          shadow = roomShadow(r.id);
        }
      }
    }
    return { dim, shadow };
  };
  for (const wl of WALLS) {
    const horiz = Math.abs(wl.x1 - wl.x0) > Math.abs(wl.z1 - wl.z0);
    const len = horiz ? Math.abs(wl.x1 - wl.x0) : Math.abs(wl.z1 - wl.z0);
    const cx = (wl.x0 + wl.x1) / 2;
    const cz = (wl.z0 + wl.z1) / 2;
    const body = wl.h - 0.03;
    const mood = roomMoodAt(cx, cz);
    // v3: darken bone TOWARD the room shadow hue by dim, never toward black.
    const wallHex = mood.dim > 0 ? mix(PAL.bone, mood.shadow, 0.5 * mood.dim) : PAL.bone;
    // sizes along-length (L) and across-thickness (T) so one path serves both axes
    const dimL = (extra = 0): number => horiz ? len + extra : WALL_T + extra;
    const dimT = (extra = 0): number => horiz ? WALL_T + extra : len + extra;
    // body: a top-lit vertex gradient replaces the flat slab (the softness lever)
    staticB.boxG(cx, body / 2, cz, dimL(), body, dimT(), wallHex, { band: 1, grad: 0.16 });
    // chamfer cap: a proud, brightened die-cut edge that catches the key light
    staticB.box(cx, body + 0.015, cz, dimL(-0.02), 0.04, dimT(-0.02), shade(wallHex, 0.1), 1);
    // WAINSCOT: a real proud lower panel (floor->chair rail) with its own grade
    staticB.boxG(cx, 0.6, cz, dimL(0.06), 0.84, dimT(0.06), shade(wallHex, -0.06), { grad: 0.22 });
    // chair-rail cap: proud, lit — a horizontal highlight line at 1.02m
    staticB.box(cx, 1.03, cz, dimL(0.1), 0.06, dimT(0.1), shade(wallHex, 0.12), 0);
    // skirting board: dark, proud, graded darker at the floor (contact shadow)
    staticB.boxG(cx, 0.1, cz, dimL(0.1), 0.2, dimT(0.1), shade(wallHex, -0.26), { grad: 0.35 });
    // pilasters: break long flat spans into panels — proud strips ~every 4.5m,
    // lit on their return edges (their own gradient reads them as columns)
    if (len > 5) {
      const n = Math.floor(len / 4.5);
      for (let k = 1; k <= n; k++) {
        const t = k / (n + 1);
        const along = (horiz ? wl.x0 : wl.z0) + (horiz ? (wl.x1 - wl.x0) : (wl.z1 - wl.z0)) * t;
        const pcx = horiz ? along : cx;
        const pcz = horiz ? cz : along;
        staticB.boxG(pcx, body / 2, pcz, horiz ? 0.24 : WALL_T + 0.09, body - 0.06, horiz ? WALL_T + 0.09 : 0.24, shade(wallHex, 0.05), { grad: 0.14 });
        // pilaster capital: a small proud block near the top
        staticB.box(pcx, body - 0.12, pcz, horiz ? 0.32 : WALL_T + 0.14, 0.1, horiz ? WALL_T + 0.14 : 0.32, shade(wallHex, 0.14), 0);
      }
    }
  }

  // wainscot: the Appeals block is the one wood room
  for (const seg of [
    { x0: -24, z0: 10, x1: -24, z1: 17 }, { x0: -18, z0: 10, x1: -18, z1: 17 },
    { x0: -24, z0: 16.9, x1: -18, z1: 16.9 }, { x0: -24, z0: 10.1, x1: -22.5, z1: 10.1 },
    { x0: -20.5, z0: 10.1, x1: -18, z1: 10.1 }, { x0: -24, z0: 6.15, x1: -18, z1: 6.15 },
  ]) {
    const horiz = Math.abs(seg.x1 - seg.x0) > Math.abs(seg.z1 - seg.z0);
    const len = horiz ? Math.abs(seg.x1 - seg.x0) : Math.abs(seg.z1 - seg.z0);
    staticB.box((seg.x0 + seg.x1) / 2, 0.65, (seg.z0 + seg.z1) / 2, horiz ? len : 0.08, 1.3, horiz ? 0.08 : len, PAL.wood, 0);
  }

  // ---- floors: one textured plane per room --------------------------------
  const floorMeshes: Mesh[] = [];
  for (const room of ROOMS) {
    const tex = makeFloorTexture(room);
    // GRAPHICS R1 (#75): floors were unlit Basic (flat, blown-out white). Lambert
    // lets them RECEIVE the key's cast shadows and take its warm falloff, so the
    // baked pools/AO read as one grounded surface instead of a lightbox — while
    // patchMaterial keeps the ceremony dim/adjudication uniform working.
    const mat = new MeshLambertMaterial({ map: tex });
    patchMaterial(mat);
    const geo = new PlaneGeometry(room.x1 - room.x0, room.z1 - room.z0);
    const mesh = new Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set((room.x0 + room.x1) / 2, 0, (room.z0 + room.z1) / 2);
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    group.add(mesh);
    floorMeshes.push(mesh);
  }

  // ---- ceiling furniture: fluorescent tubes + glow cards ------------------
  // the housing is an OPEN frame (side rails + end caps), never a solid tray:
  // both the long lens and the eye must see the gas, or the light reads dead
  for (const room of ROOMS) {
    // v3-2: warm each room's fluorescent toward its KEY WASH so the overhead
    // light is COLORED, not one flat gas-green everywhere — Intake cool-white,
    // Appeals tungsten, Gallery brass. The tube stays a bright practical; the
    // wash only bends its temperature to match the floor pools of the same room.
    const tubeHex = mix(PAL.gas, roomWash(room.id), 0.5);
    for (const [px, pz] of room.pools) {
      unlitB.box(px, 4.36, pz, 2.4, 0.09, 0.15, tubeHex, 0); // the tube itself
      for (const s of [-1, 1]) {
        unlitB.box(px, 4.42, pz + s * 0.11, 2.6, 0.05, 0.03, shade(PAL.carbon, 0.08), 0); // side rails
        unlitB.box(px + s * 1.31, 4.4, pz, 0.05, 0.08, 0.24, shade(PAL.carbon, 0.08), 0); // end caps
      }
      glowB.floorQuad(px, pz, 2.6, 1.1, 0, FULL_UV, 4.26);
    }
  }

  // ---- clerk windows (static parts; arms/prints live in entities) ---------
  const frostQuads = new QuadBatch();
  const clerkGeos: BufferGeometry[] = [];
  for (let i = 0; i < WINDOW_X.length; i++) {
    const wx = WINDOW_X[i];
    const wsh = roomMoodAt(wx, WINDOW_Z).shadow; // v3-2: room shadow hue
    // counter (manila) proud of the north wall — graded body + proud sill lip
    staticB.boxG(wx, 0.55, WINDOW_Z + 0.65, 2.2, 1.1, 0.7, PAL.manila, { grad: 0.2, dark: wsh, topLift: 0.7 });
    staticB.boxG(wx, 1.13, WINDOW_Z + 0.65, 2.3, 0.06, 0.8, shade(PAL.manila, -0.12), { grad: 0.1 });
    staticB.chamfer(wx, 1.17, WINDOW_Z + 0.65, 2.3, 0.8, PAL.manila); // 4cm proud sill lip
    // gasket-rim shadow line under the frost, where counter meets the frame
    staticB.box(wx, 1.24, WINDOW_Z + 0.4, 2.0, 0.03, 0.06, shade(PAL.carbon, 0.02), 0);
    // counter apron detail: drawer front + brass pull under the lip
    staticB.boxG(wx - 0.5, 0.82, WINDOW_Z + 1.0, 0.7, 0.28, 0.04, shade(PAL.manila, -0.22), { grad: 0.12, dark: wsh });
    staticB.box(wx - 0.5, 0.82, WINDOW_Z + 1.03, 0.22, 0.04, 0.03, PAL.brass, 0);
    // frame (carbon) — graded jambs + lintel, hex bolts at the four corners
    staticB.boxG(wx - 1.05, 2.2, WINDOW_Z + 0.35, 0.12, 2.2, 0.18, PAL.carbon, { grad: 0.18 });
    staticB.boxG(wx + 1.05, 2.2, WINDOW_Z + 0.35, 0.12, 2.2, 0.18, PAL.carbon, { grad: 0.18 });
    staticB.box(wx, 3.32, WINDOW_Z + 0.35, 2.25, 0.14, 0.18, shade(PAL.carbon, 0.06), 0);
    for (const bx of [wx - 1.05, wx + 1.05]) {
      for (const by of [1.2, 3.25]) hexBolt(staticB, bx, by, WINDOW_Z + 0.45, 0.045, 0.05, PAL.brass, 'z');
    }
    // paper slot: a true recess (dark inset) + brass lip
    staticB.box(wx, 1.22, WINDOW_Z + 0.74, 0.94, 0.08, 0.12, shade(PAL.carbon, 0.24), 0);
    staticB.box(wx, 1.18, WINDOW_Z + 0.66, 0.86, 0.06, 0.06, shade(PAL.carbon, -0.1), 0); // dark mouth
    staticB.box(wx, 1.2, WINDOW_Z + 0.79, 0.9, 0.04, 0.04, PAL.brass, 0); // brass lip
    // stamp-arm rail: the vertical carbon channel the piston travels in
    staticB.boxG(wx + 0.55, 2.95, WINDOW_Z + 0.36, 0.14, 0.9, 0.1, shade(PAL.carbon, 0.12), { grad: 0.14 });
    staticB.box(wx + 0.55, 2.95, WINDOW_Z + 0.42, 0.06, 0.86, 0.03, shade(PAL.carbon, -0.05), 0); // dark slot
    // THE LIGHTBOX: the clerk theater lives in the shallow bay between the
    // wall face and the frost pane — a lit gas panel flush on the wall, a slim
    // paperwork torso in front of it, frost in front of that. At 1.6m the
    // silhouette reads as staff; the panel reads as an office behind glass.
    unlitB.box(wx, 2.2, WINDOW_Z + 0.14, 1.9, 2.0, 0.02, shade(PAL.gas, -0.04), 0);
    // clerk silhouette: stacked paperwork torso, its own mesh (local coords;
    // windows.ts gives it the subtle shuffling animation)
    const clerkB = new Batch();
    for (let s = 0; s < 6; s++) {
      clerkB.box((s % 2 ? 0.09 : -0.07), 1.45 + s * 0.22, 0, 0.85 - s * 0.09, 0.2, 0.14, shade(PAL.ghost, -0.16 - s * 0.02), 0);
    }
    // a topmost loose sheet, slightly askew — the shuffle reads on it
    clerkB.box(0.02, 1.45 + 6 * 0.22, 0, 0.3, 0.03, 0.12, PAL.paper, 0, 0.3);
    clerkGeos.push(clerkB.build());
    // frost pane
    frostQuads.wallQuad(wx, 2.25, WINDOW_Z + 0.32, 2.0, 2.0, 0, FULL_UV);
    // window number plate (brass) — doubled, carrying the numeral from the atlas
    staticB.boxG(wx - 0.8, 3.02, WINDOW_Z + 0.46, 0.5, 0.42, 0.06, PAL.brass, { grad: 0.16, topLift: 0.5 });
    staticB.chamfer(wx - 0.8, 3.24, WINDOW_Z + 0.46, 0.5, 0.06, PAL.brass);
    hwB.wallQuad(wx - 0.8, 3.02, WINDOW_Z + 0.5, 0.4, 0.32, 0, hwAtlas.region(('win' + (i + 1)) as HardwareLabel));
  }
  const frostMat = new MeshBasicMaterial({
    map: makeFrostTexture(), transparent: true, opacity: 0.62, depthWrite: false, side: DoubleSide,
  });
  patchMaterial(frostMat);
  const frostMesh = new Mesh(frostQuads.build(), frostMat);
  group.add(frostMesh);

  // ---- Window 3 flickering tube (dynamic; entities drive intensity) -------
  const w3Mat = new MeshBasicMaterial({ color: new Color(PAL.gas), toneMapped: false });
  patchMaterial(w3Mat, { dimExempt: true });
  const w3Tube = new Mesh(new BoxGeometry(2.4, 0.07, 0.14), w3Mat);
  w3Tube.position.set(0, 3.7, WINDOW_Z + 0.7);
  group.add(w3Tube);
  const w3GlowB = new QuadBatch();
  w3GlowB.floorQuad(0, WINDOW_Z + 1.4, 3.6, 2.2, 0, FULL_UV, 0.03);
  const w3GlowMat = new MeshBasicMaterial({
    color: new Color(PAL.gas), transparent: true, opacity: 0.16, blending: AdditiveBlending, depthWrite: false, toneMapped: false,
  });
  const w3Glow = new Mesh(w3GlowB.build(), w3GlowMat);
  group.add(w3Glow);

  // ---- eye-lamp yokes (heads are instanced in entities/lamps.ts) ----------
  for (const [lx, lz] of LAMPS) {
    staticB.box(lx, LAMP_Y + 0.45, lz, 0.06, 0.9, 0.06, PAL.carbon, 0); // stem
    staticB.box(lx, LAMP_Y + 0.12, lz, 0.34, 0.06, 0.08, PAL.carbon, 0); // yoke bar
  }

  // ---- pneumatic tube network + brass fixture rings -----------------------
  for (const pts of TUBES) {
    const curve = new CatmullRomCurve3(pts.map(([x, y, z]) => new Vector3(x, y, z)));
    const segs = Math.max(12, Math.floor(curve.getLength() * 1.5));
    _m4.identity();
    staticB.absorb(new TubeGeometry(curve, segs, 0.12, 8, false), _m4.clone(), shade(PAL.carbon, 0.1), 0);
    const len = curve.getLength();
    for (let d = TUBE_RING_SPACING / 2; d < len; d += TUBE_RING_SPACING) {
      const p = curve.getPointAt(d / len);
      const tangent = curve.getTangentAt(d / len);
      _m4.lookAt(new Vector3(0, 0, 0), tangent, new Vector3(0, 1, 0)).setPosition(p.x, p.y, p.z);
      staticB.absorb(new TorusGeometry(0.16, 0.05, 6, 12), _m4.clone(), PAL.brass, 0);
    }
  }

  // tube junction boxes where the window risers meet the trunk + drip-leg stubs
  for (const wx of WINDOW_X) {
    staticB.boxG(wx, 5.5, -15.92, 0.16, 0.16, 0.16, shade(PAL.carbon, 0.14), { grad: 0.16 });
    staticB.box(wx, 5.42, -15.92, 0.2, 0.03, 0.2, shade(PAL.carbon, 0.02), 0); // seam lip
  }
  for (const tx of [8, 0, -8, -16, -24]) {
    staticB.boxG(tx, 5.28, -15.9, 0.08, 0.28, 0.08, shade(PAL.carbon, 0.1), { grad: 0.2 });
    hexBolt(staticB, tx, 5.12, -15.9, 0.06, 0.05, PAL.brass, 'y');
  }

  // ---- THE VAULT: concentric rings composed as a face ---------------------
  {
    const vx = VAULT.x + 0.35;
    const face = (r: number, tube: number, hex: number): void => {
      _m4.makeRotationY(Math.PI / 2).setPosition(vx, VAULT.h / 2, VAULT.z);
      staticB.absorb(new TorusGeometry(r, tube, 8, 28), _m4.clone(), hex, 0);
    };
    // gasket recess ring: a dark torus set INTO the wall behind the outer ring
    // so the rings read as sitting in the face, not stuck on it
    _m4.makeRotationY(Math.PI / 2).setPosition(VAULT.x + 0.12, VAULT.h / 2, VAULT.z);
    staticB.absorb(new TorusGeometry(3.95, 0.4, 8, 30), _m4.clone(), shade(PAL.carbon, -0.06), 0);
    face(3.6, 0.28, shade(PAL.carbon, 0.12));
    face(2.5, 0.22, PAL.brass);
    face(1.4, 0.2, shade(PAL.carbon, 0.18));
    // 8 hex bolts on a clock between the outer and mid rings (seeded-looking)
    const clock = [0.1, 0.85, 1.6, 2.3, 3.05, 3.9, 4.6, 5.4];
    for (const ang of clock) {
      const by = VAULT.h / 2 + Math.sin(ang) * 3.05;
      const bz = VAULT.z + Math.cos(ang) * 3.05;
      hexBolt(staticB, vx + 0.06, by, bz, 0.09, 0.14, PAL.brass, 'x');
    }
    // two hex-bolt eyes
    for (const dz of [-1.5, 1.5]) {
      _m4.makeRotationZ(Math.PI / 2).setPosition(vx, VAULT.h / 2 + 1.1, VAULT.z + dz);
      staticB.absorb(new CylinderGeometry(0.42, 0.42, 0.3, 6), _m4.clone(), PAL.brass, 0);
    }
    // ring mouth
    _m4.makeRotationY(Math.PI / 2).setPosition(vx, VAULT.h / 2 - 1.6, VAULT.z);
    staticB.absorb(new TorusGeometry(0.7, 0.14, 6, 20), _m4.clone(), shade(PAL.carbon, 0.05), 0);
    // brass inspection plate below the mouth (ADJUDICATION — NO SERVICE ENTRANCE)
    staticB.boxG(vx + 0.04, VAULT.h / 2 - 2.9, VAULT.z, 0.06, 0.7, 2.4, PAL.brass, { grad: 0.16, rotY: 0 });
    hexBolt(staticB, vx + 0.09, VAULT.h / 2 - 2.62, VAULT.z - 1.05, 0.05, 0.06, shade(PAL.brass, -0.3), 'x');
    hexBolt(staticB, vx + 0.09, VAULT.h / 2 - 3.18, VAULT.z + 1.05, 0.05, 0.06, shade(PAL.brass, -0.3), 'x');
    hwB.wallQuad(vx + 0.1, VAULT.h / 2 - 2.9, VAULT.z, 2.1, 0.56, 90, hwAtlas.region('vaultPlate'));
    // vault slab behind (graded)
    staticB.boxG(VAULT.x + 0.1, VAULT.h / 2, VAULT.z, 0.3, VAULT.h, VAULT.w, shade(PAL.bone, -0.12), { band: 1, grad: 0.2 });
  }
  // beacon: rotating amber lighthouse cone (props.ts spins it). The wide rim
  // fades to nothing via vertex alpha so the sweep never clips architecture
  // into hard triangles — light dissolves; it does not shear.
  const beaconMat = new MeshBasicMaterial({
    color: new Color(PAL.amber), transparent: true, opacity: 0.26, blending: AdditiveBlending,
    depthWrite: false, side: DoubleSide, vertexColors: true, toneMapped: false,
  });
  const beaconGeo = new CylinderGeometry(0.05, 1.15, 3.6, 12, 1, true);
  {
    const bp = beaconGeo.getAttribute('position');
    const bc = new Float32Array(bp.count * 4);
    for (let i = 0; i < bp.count; i++) {
      const a = bp.getY(i) > 0 ? 1.0 : 0.0; // apex solid, rim dissolved
      bc[i * 4] = 1; bc[i * 4 + 1] = 1; bc[i * 4 + 2] = 1; bc[i * 4 + 3] = a;
    }
    beaconGeo.setAttribute('color', new BufferAttribute(bc, 4));
  }
  const beacon = new Mesh(beaconGeo, beaconMat);
  beacon.rotation.z = Math.PI / 2;
  beacon.position.set(VAULT.x + 1.2, VAULT.beaconY - 1.4, VAULT.z);
  group.add(beacon);
  unlitB.box(VAULT.x + 0.5, VAULT.beaconY - 1.4, VAULT.z, 0.4, 0.4, 0.4, PAL.amber, 0);
  // THE MOUTH GLOW: an amber pool breathing behind the ring mouth — the sense of
  // a mind on the far side. It idles low and warms ONE STEP during adjudication
  // (props.ts drives it off the ceremony bus); additive so it never clips.
  const vaultMouthMat = new MeshBasicMaterial({
    color: new Color(PAL.amber), transparent: true, opacity: 0.05,
    blending: AdditiveBlending, depthWrite: false, side: DoubleSide, toneMapped: false,
  });
  const vaultMouthGeo = new CylinderGeometry(0.0, 0.62, 0.9, 20, 1, true);
  vaultMouthGeo.rotateZ(-Math.PI / 2); // cone opens toward +x, into the rotunda
  const vaultMouthGlow = new Mesh(vaultMouthGeo, vaultMouthMat);
  vaultMouthGlow.position.set(VAULT.x + 0.85, VAULT.h / 2 - 1.6, VAULT.z);
  group.add(vaultMouthGlow);

  // ---- THE 2-V GAUGE MANIFOLD: pressure is kept by hands --------------------
  // A brass gauge board bolted to the vault wall in the rotunda's NW corner
  // (clear of the rings, clear of the DO-NOT-STAND pool), with two dial gauges,
  // two valve wheels, and riser pipes climbing to meet the overhead trunk that
  // feeds the mouth. Curvature is permitted here (fixture caste: gauges/valves).
  {
    const MFX = -29.55; // panel face plane (proud of the x=-30 wall)
    const MZ = -4.7; // NW corner, north of the outer ring's z=-3.95 reach
    // backing board (graded carbon) + brass frame lip
    staticB.boxG(MFX - 0.09, 1.35, MZ, 0.16, 2.0, 1.7, shade(PAL.carbon, 0.08), { grad: 0.2 });
    staticB.chamfer(MFX + 0.02, 2.36, MZ, 0.16, 1.7, PAL.brass, 90);
    // a round dial gauge facing +x: brass rim + pale face + carbon needle
    const gauge = (gy: number, gz: number, needle: number): void => {
      _m4.makeRotationZ(Math.PI / 2).setPosition(MFX + 0.12, gy, gz);
      staticB.absorb(new CylinderGeometry(0.24, 0.24, 0.05, 20), _m4.clone(), shade(PAL.bone, -0.02), 0); // face
      _m4.makeRotationY(Math.PI / 2).setPosition(MFX + 0.15, gy, gz);
      staticB.absorb(new TorusGeometry(0.25, 0.03, 8, 22), _m4.clone(), PAL.brass, 0); // rim
      // needle: a thin carbon box tilted to the gauge's kept pressure
      _m4.makeRotationX(needle).setPosition(0, 0, 0);
      _m4.premultiply(new Matrix4().makeTranslation(MFX + 0.16, gy, gz));
      staticB.absorb(new BoxGeometry(0.02, 0.2, 0.02).translate(0, 0.08, 0), _m4.clone(), shade(PAL.carbon, 0.3), 0);
      hexBolt(staticB, MFX + 0.16, gy, gz, 0.03, 0.05, PAL.brass, 'x'); // hub
    };
    gauge(1.85, MZ - 0.42, 0.5);
    gauge(1.85, MZ + 0.42, -0.85);
    // two valve wheels below the dials (torus rim + carbon spokes)
    for (const vz of [MZ - 0.42, MZ + 0.42]) {
      _m4.makeRotationY(Math.PI / 2).setPosition(MFX + 0.14, 1.15, vz);
      staticB.absorb(new TorusGeometry(0.17, 0.025, 6, 18), _m4.clone(), PAL.brass, 0);
      for (let s = 0; s < 3; s++) {
        _m4.makeRotationX((s * Math.PI) / 3).setPosition(MFX + 0.14, 1.15, vz);
        staticB.absorb(new BoxGeometry(0.02, 0.32, 0.02), _m4.clone(), shade(PAL.brass, -0.15), 0);
      }
      staticB.box(MFX + 0.16, 1.15, vz, 0.04, 0.06, 0.06, shade(PAL.carbon, 0.28), 0); // valve stem cap
    }
    // riser pipes climbing to the overhead trunk (the pressure line to the mouth)
    for (const pz of [MZ - 0.42, MZ + 0.42]) {
      _m4.identity().setPosition(MFX - 0.02, 3.4, pz);
      staticB.absorb(new CylinderGeometry(0.06, 0.06, 3.6, 8), _m4.clone(), shade(PAL.brass, -0.1), 0);
      for (const ry of [2.0, 3.2, 4.6]) {
        _m4.makeRotationX(Math.PI / 2).setPosition(MFX - 0.02, ry, pz);
        staticB.absorb(new TorusGeometry(0.08, 0.02, 6, 12), _m4.clone(), PAL.brass, 0); // pipe collar
      }
    }
    // manifold pilot: amber status (the pressure is nominal, for now)
    unlitB.box(MFX + 0.14, 2.2, MZ, 0.05, 0.06, 0.05, PAL.amber, 0);
    // gasket rim where the board meets the floor + the wall (dark inset)
    staticB.boxG(MFX - 0.02, 0.16, MZ, 0.2, 0.32, 1.8, shade(PAL.carbon, -0.04), { grad: 0.4 });
  }

  // ---- furniture ----------------------------------------------------------
  for (let di = 0; di < DESKS.length; di++) {
    const d = DESKS[di];
    const dsh = roomMoodAt(d.x, d.z).shadow; // v3-2: share the room's shadow hue
    staticB.boxG(d.x, 0.72, d.z, 1.6, 0.07, 0.8, PAL.manila, { grad: 0.08, dark: dsh, topLift: 0.7 });
    staticB.chamfer(d.x, 0.76, d.z, 1.6, 0.8, PAL.manila); // 2cm proud top edge
    staticB.boxG(d.x - 0.7, 0.36, d.z, 0.08, 0.72, 0.7, shade(PAL.manila, -0.2), { grad: 0.2, dark: dsh });
    staticB.boxG(d.x + 0.7, 0.36, d.z, 0.08, 0.72, 0.7, shade(PAL.manila, -0.2), { grad: 0.2, dark: dsh });
    // modesty panel: a thin back box between the pedestals
    staticB.boxG(d.x, 0.5, d.z - 0.34, 1.36, 0.46, 0.03, shade(PAL.manila, -0.3), { grad: 0.16, dark: dsh });
    // side drawer face + brass pull (the desk owns paperwork of its own)
    staticB.boxG(d.x + 0.45, 0.56, d.z + 0.41, 0.44, 0.22, 0.03, shade(PAL.manila, -0.28), { grad: 0.12 });
    staticB.box(d.x + 0.45, 0.56, d.z + 0.435, 0.16, 0.035, 0.025, PAL.brass, 0);
    // black platen knobs, one each side of the platen (1cm cylinders)
    for (const kx of [d.x - 0.25, d.x + 0.25]) {
      _m4.makeRotationZ(Math.PI / 2).setPosition(kx, 1.0, d.z - 0.26);
      staticB.absorb(new CylinderGeometry(0.03, 0.03, 0.04, 8), _m4.clone(), shade(PAL.carbon, 0.28), 0);
    }
    // in-tray beside the typewriter (paired with the out-tray, seeded fill)
    staticB.boxG(d.x + 0.55, 0.78, d.z - 0.22, 0.32, 0.03, 0.3, shade(PAL.manila, -0.14), { grad: 0.1 });
    staticB.box(d.x + 0.55, 0.8, d.z - 0.22, 0.24, 0.02 + (di % 3) * 0.01, 0.26, shade(PAL.paper, -0.04), 0, 0.08);
    // typewriter: carbon slab + platen + paper bail + key rows (FP-legible)
    staticB.box(d.x, 0.84, d.z - 0.1, 0.5, 0.18, 0.4, PAL.carbon, 0);
    _m4.makeRotationZ(Math.PI / 2).setPosition(d.x, 1.0, d.z - 0.26);
    staticB.absorb(new CylinderGeometry(0.045, 0.045, 0.46, 8), _m4.clone(), shade(PAL.carbon, 0.22), 0);
    staticB.box(d.x, 0.98, d.z - 0.22, 0.44, 0.12, 0.05, shade(PAL.carbon, 0.15), 0);
    for (let r = 0; r < 3; r++) {
      for (let k = 0; k < 8; k++) {
        staticB.box(
          d.x - 0.155 + k * 0.045 + (r % 2) * 0.012, 0.945 - r * 0.014, d.z + 0.06 + r * 0.055,
          0.032, 0.02, 0.032, shade(PAL.carbon, 0.4), 0
        );
      }
    }
    // out-tray paper stack, top sheet askew
    staticB.box(d.x - 0.5, 0.775, d.z + 0.16, 0.26, 0.045, 0.34, shade(PAL.paper, -0.03), 0);
    staticB.box(d.x - 0.5, 0.802, d.z + 0.16, 0.24, 0.008, 0.32, PAL.paper, 0, 0.12 + (di % 3) * 0.09);
    // chair (v3-2: furniture >15cm → boxG top-lit gradient, room-hue shadow)
    staticB.boxG(d.x, 0.45, d.z + 0.75, 0.5, 0.06, 0.5, shade(PAL.manila, -0.1), { grad: 0.1, dark: dsh });
    staticB.boxG(d.x, 0.75, d.z + 0.98, 0.5, 0.6, 0.06, shade(PAL.manila, -0.1), { grad: 0.2, dark: dsh });
  }
  for (const L of [LECTERN, FORM3_LECTERN]) {
    const lsh = roomMoodAt(L.x, L.z).shadow; // v3-2: room shadow hue
    // graded shaft + mid collar (taper illusion) + foot ring
    staticB.boxG(L.x, 0.5, L.z, 0.42, 1.0, 0.34, shade(PAL.manila, -0.05), { grad: 0.2, dark: lsh, topLift: 0.6 });
    staticB.box(L.x, 0.64, L.z, 0.5, 0.06, 0.42, shade(PAL.manila, 0.06), 0);
    staticB.boxG(L.x, 0.07, L.z, 0.6, 0.14, 0.5, shade(PAL.manila, -0.18), { grad: 0.35, dark: lsh });
    staticB.chamfer(L.x, 0.14, L.z, 0.6, 0.5, PAL.manila);
    // angled reading top (12deg) + paper-stop lip on the near edge
    _m4.makeRotationX((-12 * Math.PI) / 180).setPosition(L.x, 1.14, L.z);
    staticB.absorb(new BoxGeometry(0.6, 0.05, 0.5), _m4.clone(), PAL.manila, 0);
    staticB.box(L.x, 1.11, L.z + 0.23, 0.6, 0.06, 0.03, shade(PAL.manila, 0.1), 0);
    if (L === FORM3_LECTERN) {
      staticB.box(L.x, 0.72, L.z + 0.16, 0.46, 0.18, 0.02, PAL.brass, 0);
      hwB.wallQuad(L.x, 0.72, L.z + 0.175, 0.4, 0.13, 0, hwAtlas.region('consent'));
    }
  }
  for (const b of BENCHES) {
    const isRecess = b.z > 10 && b.x < -12;
    const hex = isRecess ? PAL.ghost : PAL.manila;
    const bsh = roomMoodAt(b.x, b.z).shadow; // v3-2: room shadow (Recess resolves NEUTRAL)
    // Recess keeps its deliberate colorless identity: neutral shadow, NO warm rim.
    staticB.boxG(b.x, 0.42, b.z, 1.8, 0.08, 0.55, hex, { grad: 0.08, dark: bsh, topLift: isRecess ? 0 : 0.5 });
    staticB.chamfer(b.x, 0.46, b.z, 1.8, 0.55, hex); // 2cm proud slab edge
    staticB.boxG(b.x - 0.75, 0.21, b.z, 0.08, 0.42, 0.5, PAL.carbon, { grad: 0.25 });
    staticB.boxG(b.x + 0.75, 0.21, b.z, 0.08, 0.42, 0.5, PAL.carbon, { grad: 0.25 });
    for (const lx of [b.x - 0.75, b.x + 0.75]) staticB.box(lx, 0.04, b.z, 0.16, 0.06, 0.6, shade(PAL.carbon, 0.1), 0); // floor gussets
    if (!isRecess) staticB.boxG(b.x, 0.32, b.z + 0.26, 1.7, 0.14, 0.03, shade(hex, -0.14), { grad: 0.14, dark: bsh }); // front apron
  }

  // dispenser: dark mass + red pilot (pulse driven by props.ts). Front = +z.
  const DZ = DISPENSER.z, DX = DISPENSER.x;
  const dish = roomMoodAt(DX, DZ).shadow; // v3-2: Intake cold slate-teal shadow
  staticB.boxG(DX, 0.65, DZ, 0.8, 1.3, 0.8, shade(PAL.carbon, 0.06), { grad: 0.2, dark: dish, topLift: 0.6 });
  // gasket apron where it meets the floor (dark 2cm skirt, contact grade)
  staticB.boxG(DX, 0.06, DZ, 0.9, 0.12, 0.9, shade(PAL.carbon, -0.06), { grad: 0.4, dark: dish });
  // brass crown + chamfer lip
  staticB.boxG(DX, 1.36, DZ, 0.6, 0.12, 0.6, PAL.brass, { grad: 0.14, topLift: 0.6 });
  staticB.chamfer(DX, 1.43, DZ, 0.6, 0.6, PAL.brass);
  // front inspection plate with two bolt dots (recessed panel read)
  inspectionPlate(staticB, DX - 0.18, 0.55, DZ + 0.4, 0.34, 0.44, shade(PAL.carbon, 0.14), 'z');
  // TAKE A TICKET stencil above the slot (atlas)
  hwB.wallQuad(DX, 1.14, DZ + 0.41, 0.5, 0.11, 0, hwAtlas.region('takeTicket'));
  // ticket slot: recessed dark mouth + brass return lip + a stub tray below
  staticB.box(DX + 0.2, 0.98, DZ + 0.4, 0.34, 0.1, 0.05, shade(PAL.carbon, -0.05), 0); // dark recess
  staticB.box(DX + 0.2, 0.93, DZ + 0.43, 0.4, 0.03, 0.06, PAL.brass, 0); // return lip
  staticB.boxG(DX + 0.2, 0.84, DZ + 0.44, 0.34, 0.02, 0.12, shade(PAL.manila, -0.1), { grad: 0.1 }); // stub tray
  // side louver vent (the fixture kit's vent, replacing the plain bars)
  louver(staticB, DX + 0.41, 0.6, DZ, 0.5, 0.5, 4, 'x');
  const pilotMat = new MeshBasicMaterial({ color: new Color(PAL.stampRed) });
  const pilotLamp = new Mesh(new BoxGeometry(0.07, 0.07, 0.07), pilotMat);
  pilotLamp.position.set(DISPENSER.x + 0.28, 1.18, DISPENSER.z + 0.41);
  group.add(pilotLamp);

  // split-flap housing (board texture applied by props.ts entity)
  unlitB.box(SPLITFLAP.x, SPLITFLAP.y, SPLITFLAP.z - 0.06, SPLITFLAP.w + 0.2, SPLITFLAP.h + 0.2, 0.12, shade(PAL.carbon, 0.05), 0);

  // turnstile: posts static, arms dynamic
  for (const pz of [TURNSTILE.z - 1.3, TURNSTILE.z + 1.3]) {
    staticB.boxG(TURNSTILE.x, 0.55, pz, 0.14, 1.1, 0.14, PAL.carbon, { grad: 0.22 });
    staticB.box(TURNSTILE.x, 1.13, pz, 0.2, 0.06, 0.2, PAL.brass, 0); // brass cap
    staticB.chamfer(TURNSTILE.x, 1.16, pz, 0.2, 0.2, PAL.brass);
    // base gusset: a 45deg wedge at the foot
    _m4.makeRotationX(Math.PI / 4).setPosition(TURNSTILE.x, 0.18, pz + (pz > TURNSTILE.z ? -0.13 : 0.13));
    staticB.absorb(new BoxGeometry(0.12, 0.16, 0.16), _m4.clone(), shade(PAL.carbon, 0.12), 0);
  }
  // hub: a visible 8-seg drum with a hex-bolt cap; the arms turn around it
  _m4.identity().setPosition(TURNSTILE.x, 0.95, TURNSTILE.z);
  staticB.absorb(new CylinderGeometry(0.15, 0.15, 0.62, 8), _m4.clone(), shade(PAL.carbon, 0.16), 0);
  hexBolt(staticB, TURNSTILE.x, 1.27, TURNSTILE.z, 0.07, 0.05, PAL.brass, 'y');
  // amber status pilot (turnstile is status, never consequence)
  unlitB.box(TURNSTILE.x, 1.15, TURNSTILE.z + 1.38, 0.05, 0.06, 0.03, PAL.amber, 0);
  const turnstileArms = new Group();
  const armMat = new MeshBasicMaterial({ color: new Color(PAL.brass) });
  patchMaterial(armMat);
  for (let a = 0; a < 3; a++) {
    const armGeo = new BoxGeometry(0.06, 0.06, 0.9);
    armGeo.translate(0, 0, 0.5); // arm extends outward from the hub
    const arm = new Mesh(armGeo, armMat);
    arm.rotation.y = (a * Math.PI * 2) / 3;
    turnstileArms.add(arm);
  }
  turnstileArms.position.set(TURNSTILE.x, 0.95, TURNSTILE.z);
  group.add(turnstileArms);

  // intake ropes funneling past FORM 3
  for (const [rx, rz] of [[16.5, -6.5], [16.5, -2], [19.5, -6.5], [19.5, -2], [16.5, 1.5], [19.5, 1.5]] as [number, number][]) {
    staticB.box(rx, 0.5, rz, 0.08, 1.0, 0.08, PAL.carbon, 0);
    staticB.box(rx, 1.0, rz, 0.16, 0.05, 0.16, PAL.brass, 0);
  }
  for (const [x0, z0, x1, z1] of [[16.5, -6.5, 16.5, -2], [19.5, -6.5, 19.5, -2], [16.5, -2, 16.5, 1.5], [19.5, -2, 19.5, 1.5]] as [number, number, number, number][]) {
    staticB.box((x0 + x1) / 2, 0.92, (z0 + z1) / 2, Math.abs(x1 - x0) || 0.05, 0.05, Math.abs(z1 - z0) || 0.05, shade(PAL.carbon, 0.12), 0);
  }

  // photocopier (identity reissuance) in the Registry corner. Front = north (-z).
  staticB.boxG(COPIER.x, 0.6, COPIER.z, 1.6, 1.2, 1.1, shade(PAL.ghost, -0.15), { grad: 0.18 });
  staticB.boxG(COPIER.x, 1.26, COPIER.z, 1.5, 0.12, 1.0, shade(PAL.carbon, 0.1), { grad: 0.12 });
  staticB.chamfer(COPIER.x, 1.33, COPIER.z, 1.5, 1.0, shade(PAL.carbon, 0.1));
  staticB.boxG(COPIER.x, 0.06, COPIER.z, 1.7, 0.12, 1.2, shade(PAL.carbon, -0.04), { grad: 0.4 }); // floor gasket apron
  // paper-exit tray + lip on the north face
  staticB.boxG(COPIER.x, 0.9, COPIER.z - 0.58, 1.0, 0.03, 0.28, shade(PAL.ghost, -0.04), { grad: 0.1 });
  staticB.box(COPIER.x, 0.86, COPIER.z - 0.71, 1.0, 0.05, 0.04, shade(PAL.carbon, 0.1), 0);
  // control strip (static) + 3 dynamic indicator lamps (amber, amber, red) that
  // stay COLD until a REISSUE — props.ts lights them and sweeps the scan line.
  staticB.box(COPIER.x - 0.35, 1.1, COPIER.z - 0.56, 0.72, 0.16, 0.04, shade(PAL.carbon, 0.16), 0);
  const copierPilots: Mesh[] = [];
  const copierBaseHex = [shade(PAL.amber, -0.55), shade(PAL.amber, -0.55), shade(PAL.stampRed, -0.5)];
  copierBaseHex.forEach((h, ci) => {
    const pm = new MeshBasicMaterial({ color: new Color(h) });
    patchMaterial(pm, { dimExempt: true });
    const pilot = new Mesh(new BoxGeometry(0.06, 0.06, 0.02), pm);
    pilot.position.set(COPIER.x - 0.55 + ci * 0.14, 1.1, COPIER.z - 0.59);
    group.add(pilot);
    copierPilots.push(pilot);
  });
  // the scan line: a dim gas bar that brightens and sweeps across the lid glass
  const copierScanMat = new MeshBasicMaterial({ color: new Color(shade(PAL.gas, -0.4)) });
  patchMaterial(copierScanMat, { dimExempt: true });
  const copierScan = new Mesh(new BoxGeometry(1.2, 0.04, 0.03), copierScanMat);
  copierScan.position.set(COPIER.x, 1.2, COPIER.z + 0.56);
  group.add(copierScan);
  // side louver vent on the east face
  louver(staticB, COPIER.x + 0.81, 0.62, COPIER.z, 0.9, 0.5, 4, 'x');

  // ---- W2 PHYSICAL-BENCH STATIONS: real hardware replacing lectern proxies --
  // All four face +z (south) into their room. Positions mirror layout.ts.
  {
    // 1) JUNCTION SWITCHBOARD (M-C: promoted to the Mail Room, on the trunk branch)
    const jx = JUNCTION_FULL.x, jz = JUNCTION_FULL.z;
    staticB.boxG(jx, 0.6, jz, 1.4, 1.2, 0.55, shade(PAL.carbon, 0.08), { grad: 0.2 });
    staticB.boxG(jx, 0.04, jz, 1.5, 0.1, 0.65, shade(PAL.carbon, -0.05), { grad: 0.4 }); // gasket apron
    // angled control desk with a lever bank
    _m4.makeRotationX((-16 * Math.PI) / 180).setPosition(jx, 1.24, jz + 0.18);
    staticB.absorb(new BoxGeometry(1.4, 0.06, 0.34), _m4.clone(), shade(PAL.carbon, 0.16), 0);
    staticB.chamfer(jx, 1.28, jz + 0.14, 1.4, 0.34, shade(PAL.carbon, 0.16));
    for (let l = 0; l < 6; l++) {
      const lx = jx - 0.5 + l * 0.2;
      staticB.box(lx, 1.34, jz + 0.16, 0.03, 0.02, 0.14, shade(PAL.carbon, 0.3), 0); // slot
      _m4.makeRotationX((-30 + (l % 2) * 45) * Math.PI / 180).setPosition(lx, 1.42, jz + 0.16);
      staticB.absorb(new BoxGeometry(0.025, 0.16, 0.025), _m4.clone(), PAL.brass, 0); // lever
      staticB.box(lx, 1.5, jz + 0.14, 0.05, 0.05, 0.05, shade(PAL.brass, 0.1), 0); // knob
    }
    // vertical glyph board rising above the cabinet
    staticB.boxG(jx, 2.05, jz - 0.18, 1.5, 1.1, 0.12, shade(PAL.carbon, 0.05), { grad: 0.16 });
    for (const cxb of [jx - 0.75, jx + 0.75]) hexBolt(staticB, cxb, 2.05, jz - 0.11, 0.04, 0.05, PAL.brass, 'z');
    hwB.wallQuad(jx, 2.1, jz - 0.11, 1.32, 0.86, 0, hwAtlas.region('junctionBoard'));
    hwB.wallQuad(jx, 1.32, jz + 0.29, 0.9, 0.13, 0, hwAtlas.region('nameJunction'));
    staticB.box(jx, 1.32, jz + 0.28, 0.94, 0.16, 0.02, PAL.brass, 0);

    // 2) DISCREPANCY WINDOW (M-C: promoted to Inspection Row, forensic row)
    const cx = DISC_FULL.x, cz = DISC_FULL.z;
    staticB.boxG(cx, 0.55, cz, 1.5, 1.1, 0.7, PAL.manila, { grad: 0.2 });
    staticB.boxG(cx, 1.12, cz, 1.6, 0.06, 0.8, shade(PAL.manila, -0.1), { grad: 0.1 });
    staticB.chamfer(cx, 1.16, cz, 1.6, 0.8, PAL.manila);
    staticB.boxG(cx, 0.05, cz, 1.6, 0.1, 0.8, shade(PAL.manila, -0.22), { grad: 0.4 });
    // inspection glass panel (frost handled below by frostQuads) + a loupe arm
    staticB.box(cx + 0.5, 1.2, cz + 0.2, 0.05, 0.5, 0.05, shade(PAL.carbon, 0.14), 0); // arm post
    _m4.makeRotationZ((-40 * Math.PI) / 180).setPosition(cx + 0.35, 1.45, cz + 0.24);
    staticB.absorb(new BoxGeometry(0.4, 0.04, 0.04), _m4.clone(), shade(PAL.carbon, 0.2), 0); // arm
    _m4.makeRotationX(Math.PI / 2).setPosition(cx + 0.12, 1.5, cz + 0.26);
    staticB.absorb(new TorusGeometry(0.11, 0.02, 6, 16), _m4.clone(), PAL.brass, 0); // loupe ring
    frostQuads.wallQuad(cx + 0.12, 1.5, cz + 0.265, 0.2, 0.2, 0, FULL_UV); // loupe glass
    hwB.wallQuad(cx, 1.32, cz + 0.41, 0.9, 0.14, 0, hwAtlas.region('nameDisc'));
    staticB.box(cx, 1.32, cz + 0.4, 0.94, 0.17, 0.02, PAL.brass, 0);

    // 3) TERMINAL SUPPORT BAY (archive, north): CRT bank + cable runs
    const tx = -15, tz = -15.3;
    staticB.boxG(tx, 0.42, tz, 1.7, 0.84, 0.7, shade(PAL.carbon, 0.05), { grad: 0.2 }); // bench
    staticB.chamfer(tx, 0.85, tz, 1.7, 0.7, shade(PAL.carbon, 0.1));
    staticB.boxG(tx, 0.05, tz, 1.8, 0.1, 0.8, shade(PAL.carbon, -0.05), { grad: 0.4 });
    for (let m = 0; m < 3; m++) {
      const mx = tx - 0.55 + m * 0.55;
      staticB.boxG(mx, 1.1, tz - 0.05, 0.44, 0.4, 0.4, shade(PAL.ghost, -0.28), { grad: 0.18 }); // CRT housing
      staticB.box(mx, 1.1, tz + 0.16, 0.36, 0.3, 0.04, shade(PAL.carbon, 0.08), 0); // bezel
      unlitB.box(mx, 1.1, tz + 0.18, 0.3, 0.24, 0.02, shade(PAL.amber, -0.32), 0); // dim phosphor screen (amber, canon-safe)
      staticB.box(mx, 0.86, tz + 0.02, 0.3, 0.06, 0.34, shade(PAL.ghost, -0.35), 0); // keyboard shelf
      // cable run draping to the floor
      _m4.identity().setPosition(mx + 0.18, 0.55, tz + 0.28);
      staticB.absorb(new CylinderGeometry(0.02, 0.02, 0.7, 6), _m4.clone(), shade(PAL.carbon, 0.06), 0);
    }
    hwB.wallQuad(tx, 1.5, tz + 0.36, 0.95, 0.14, 0, hwAtlas.region('nameTerm'));

    // 4) ARMATURE ALIGNMENT (hall, north beside dept6): dial console + stamp bed
    const ax = -11, az = -15.3;
    staticB.boxG(ax, 0.55, az, 1.3, 1.1, 0.62, shade(PAL.carbon, 0.09), { grad: 0.2 });
    staticB.boxG(ax, 0.05, az, 1.4, 0.1, 0.72, shade(PAL.carbon, -0.05), { grad: 0.4 });
    // dial console face with three brass dials
    staticB.box(ax - 0.28, 1.05, az + 0.32, 0.7, 0.5, 0.04, shade(PAL.carbon, 0.16), 0);
    for (let di2 = 0; di2 < 3; di2++) {
      _m4.makeRotationX(Math.PI / 2).setPosition(ax - 0.5 + di2 * 0.22, 1.05, az + 0.35);
      staticB.absorb(new CylinderGeometry(0.08, 0.08, 0.03, 12), _m4.clone(), PAL.brass, 0);
      staticB.box(ax - 0.5 + di2 * 0.22, 1.05, az + 0.37, 0.01, 0.06, 0.02, shade(PAL.carbon, 0.2), 0); // needle
    }
    // test-stamp bed: a flat bed + a vertical press arm with a stamp head
    staticB.boxG(ax + 0.42, 1.16, az + 0.05, 0.34, 0.05, 0.34, shade(PAL.manila, -0.05), { grad: 0.1 }); // bed
    staticB.box(ax + 0.58, 1.4, az - 0.08, 0.05, 0.5, 0.05, shade(PAL.carbon, 0.16), 0); // press column
    staticB.box(ax + 0.42, 1.62, az + 0.02, 0.28, 0.06, 0.18, shade(PAL.carbon, 0.2), 0); // press head yoke
    staticB.box(ax + 0.42, 1.5, az + 0.05, 0.1, 0.14, 0.1, PAL.brass, 0); // stamp plunger
    hwB.wallQuad(ax, 1.32, az + 0.33, 0.85, 0.14, 0, hwAtlas.region('nameArm'));
    staticB.box(ax, 1.32, az + 0.32, 0.9, 0.17, 0.02, PAL.brass, 0);
  }

  // ---- M-C SERVICE RING FIXTURES: promotions, teasers, dread ---------------
  // A compact greeble helper: a station cabinet (graded body + gasket apron +
  // brass crown + chamfer) that the small teasers and dread props all reuse.
  const cabinet = (x: number, z: number, w: number, h: number, d: number, hex: number): void => {
    staticB.boxG(x, h / 2, z, w, h, d, hex, { grad: 0.2 });
    staticB.boxG(x, 0.05, z, w + 0.1, 0.1, d + 0.1, shade(hex, -0.06), { grad: 0.4 }); // floor gasket apron
    staticB.boxG(x, h + 0.06, z, w - 0.05, 0.12, d - 0.05, PAL.brass, { grad: 0.14 }); // crown
    staticB.chamfer(x, h + 0.13, z, w - 0.05, d - 0.05, PAL.brass);
  };
  {
    // 8-R GRADE-1 TAP (Rotunda trunk wall): a slim single-lever tap on the pipe.
    const tx = JUNCTION_TAP.x, tz = JUNCTION_TAP.z;
    cabinet(tx, tz, 0.8, 1.05, 0.4, shade(PAL.carbon, 0.08));
    staticB.box(tx, 1.18, tz + 0.16, 0.03, 0.02, 0.12, shade(PAL.carbon, 0.3), 0); // lever slot
    _m4.makeRotationX((-24 * Math.PI) / 180).setPosition(tx, 1.28, tz + 0.16);
    staticB.absorb(new BoxGeometry(0.025, 0.16, 0.025), _m4.clone(), PAL.brass, 0); // one lever
    staticB.box(tx, 1.36, tz + 0.14, 0.05, 0.05, 0.05, shade(PAL.brass, 0.1), 0); // knob
    staticB.box(tx, 0.72, tz + 0.21, 0.5, 0.16, 0.02, PAL.brass, 0); // name plate ground
    hwB.wallQuad(tx, 0.72, tz + 0.225, 0.44, 0.13, 0, hwAtlas.region('nameJunction'));
    louver(staticB, tx + 0.41, 0.55, tz, 0.4, 0.4, 3, 'x');

    // 5-D GRADE-0/1 INTRO (Window Row): a single shuttered inspection counter.
    const dx = DISC_TEASER.x, dz = DISC_TEASER.z;
    staticB.boxG(dx, 0.55, dz, 1.3, 1.1, 0.66, PAL.manila, { grad: 0.2 });
    staticB.boxG(dx, 1.12, dz, 1.4, 0.06, 0.76, shade(PAL.manila, -0.1), { grad: 0.1 });
    staticB.chamfer(dx, 1.16, dz, 1.4, 0.76, PAL.manila);
    staticB.boxG(dx, 0.05, dz, 1.4, 0.1, 0.76, shade(PAL.manila, -0.22), { grad: 0.4 });
    staticB.box(dx, 1.22, dz - 0.2, 1.1, 0.5, 0.04, shade(PAL.carbon, 0.1), 0); // shutter (closed)
    staticB.box(dx, 0.72, dz + 0.36, 0.5, 0.16, 0.02, PAL.brass, 0);
    hwB.wallQuad(dx, 0.72, dz + 0.375, 0.44, 0.13, 0, hwAtlas.region('nameDisc'));

    // 5-D FORENSIC ROW extra wickets flanking the full window (Inspection Row).
    for (const wx of [DISC_FULL.x - 3.2, DISC_FULL.x + 3.2]) {
      staticB.boxG(wx, 0.55, DISC_FULL.z, 1.4, 1.1, 0.66, PAL.manila, { grad: 0.2 });
      staticB.chamfer(wx, 1.13, DISC_FULL.z, 1.4, 0.7, PAL.manila);
      staticB.boxG(wx, 0.05, DISC_FULL.z, 1.5, 0.1, 0.76, shade(PAL.manila, -0.22), { grad: 0.4 });
      inspectionPlate(staticB, wx, 1.2, DISC_FULL.z + 0.34, 0.5, 0.34, shade(PAL.carbon, 0.12), 'z');
      unlitB.box(wx, 1.5, DISC_FULL.z - 0.05, 0.3, 0.02, 0.3, shade(PAL.amber, -0.28), 0); // inspection lamp
    }

    // 5-M CRADLE BANK (Mail Room): a parcel-cradle shelf, tractor-feed tags,
    // canister glow — the conveyance origins.
    const clx = 25, clz = -24.2;
    staticB.boxG(clx, 0.9, clz, 6.4, 0.08, 0.7, shade(PAL.carbon, 0.06), { grad: 0.12 }); // shelf
    for (const bx of [clx - 2.4, clx - 0.8, clx + 0.8, clx + 2.4]) {
      staticB.boxG(bx, 0.62, clz, 0.7, 0.5, 0.5, shade(PAL.ghost, -0.2), { grad: 0.16 }); // cradle
      staticB.box(bx, 0.9, clz, 0.6, 0.06, 0.42, PAL.brass, 0); // brass lip
      staticB.box(bx, 1.0, clz, 0.32, 0.2, 0.3, shade(PAL.paper, -0.03), 0, 0.1); // a canister/parcel
      glowB.floorQuad(bx, clz + 0.1, 0.8, 0.5, 0, FULL_UV, 0.94);
    }
    staticB.boxG(clx, 1.5, clz - 0.32, 6.6, 1.0, 0.1, shade(PAL.carbon, 0.05), { grad: 0.16 }); // backboard
    hwB.wallQuad(clx, 1.5, clz - 0.26, 1.4, 0.4, 0, hwAtlas.region('junctionBoard'));

    // THE DESCENT shaft mouth (Stairhead): a dark recessed pit + brass railing.
    // The stair itself lands in M-D; for now the mouth reads as a threshold and
    // the phosphor wash (added with the Dept-6 green below) rises out of it.
    const sx = -22, sz = -21;
    // a proud machined coaming around the mouth (graded carbon) + a lit brass
    // nosing that catches the phosphor rising out of the shaft — the threshold
    // reads as engineered, not painted; the pit is a made thing, and it is deep.
    for (const [ez, ew, ed] of [[sz - 1.35, 3.2, 0.18], [sz + 1.35, 3.2, 0.18]] as [number, number, number][]) {
      staticB.boxG(sx, 0.09, ez, ew, 0.18, ed, shade(PAL.carbon, 0.02), { grad: 0.4 });
    }
    for (const ex of [sx - 1.55, sx + 1.55]) {
      staticB.boxG(ex, 0.09, sz, 0.18, 0.18, 2.9, shade(PAL.carbon, 0.02), { grad: 0.4 });
    }
    staticB.chamfer(sx, 0.19, sz, 3.2, 2.9, PAL.brass); // lit brass nosing on the coaming
    staticB.box(sx, 0.02, sz, 3.0, 0.04, 2.6, shade(PAL.carbon, -0.08), 0); // pit mouth: a dark frame on the floor (the shaft lands in M-D)
    staticB.box(sx, 0.03, sz, 2.4, 0.02, 2.0, shade(PAL.carbon, -0.2), 0); // darker inner mouth
    for (const [rx, rz] of [[sx - 1.4, sz], [sx + 1.4, sz], [sx, sz - 1.2], [sx, sz + 1.2]] as [number, number][]) {
      staticB.boxG(rx, 0.5, rz, 0.08, 1.0, 0.08, PAL.carbon, { grad: 0.2 }); // rail post
      staticB.box(rx, 1.0, rz, 0.16, 0.05, 0.16, PAL.brass, 0);
    }
    // rail rungs around the pit (two long, two short)
    staticB.box(sx, 0.95, sz - 1.2, 2.9, 0.05, 0.05, PAL.brass, 0);
    staticB.box(sx, 0.95, sz + 1.2, 2.9, 0.05, 0.05, PAL.brass, 0);

    // RETENTION ANNEX (west wall x=-38): a deep drawer canyon, brass pulls.
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 4; c++) {
        const dz2 = 8 + c * 2.0, dy = 0.5 + r * 0.6;
        staticB.boxG(-37.6, dy, dz2, 0.5, 0.52, 1.7, PAL.manila, { grad: 0.14 });
        staticB.box(-37.32, dy, dz2, 0.06, 0.045, 0.3, PAL.brass, 0);
      }
    }
    // ENGRAVING OFFICE: a workbench with brass plaque blanks + a struck bar.
    cabinet(-34, 0, 1.8, 0.9, 0.9, PAL.wood);
    for (const oz of [-0.5, 0, 0.5]) staticB.box(-34, 0.96, oz, 0.7, 0.03, 0.34, PAL.brass, 0); // blanks
    staticB.box(-34, 1.3, 0.3, 0.05, 0.7, 0.05, shade(PAL.carbon, 0.16), 0); // press column
    staticB.box(-34, 1.62, 0.28, 0.4, 0.06, 0.2, shade(PAL.carbon, 0.2), 0); // press head
    // LOST & FOUND ANNEX (west wall): open shelving of claimed property.
    for (let s = 0; s < 4; s++) {
      staticB.boxG(-37.5, 0.5 + s * 0.7, -11, 0.7, 0.05, 6.0, shade(PAL.ghost, -0.14), { grad: 0.1 });
      for (let i = 0; i < 4; i++) {
        staticB.box(-37.5, 0.62 + s * 0.7, -13.5 + i * 1.6, 0.5, 0.24, 0.5, shade(PAL.manila, -0.08 - (i % 2) * 0.05), 0, 0.08);
      }
    }
    // COPIER ROOM alcove: the grade-5 DISPOSAL HATCH shutter + a sealed unit.
    staticB.boxG(-36.5, 0.6, 14, 2.6, 1.2, 0.9, shade(PAL.ghost, -0.18), { grad: 0.18 }); // sealed copier
    staticB.box(-37.9, 1.4, 14, 0.06, 1.0, 1.4, shade(PAL.carbon, 0.06), 0); // hatch frame on x=-38 wall
    staticB.box(-37.86, 1.4, 14, 0.04, 0.86, 1.2, shade(PAL.carbon, -0.06), 0); // dark shutter mouth
    for (const hz of [13.4, 14.6]) hexBolt(staticB, -37.82, 1.4, hz, 0.05, 0.06, PAL.brass, 'x');
    unlitB.box(-36.5, 1.2, 13.54, 1.0, 0.04, 0.03, shade(PAL.stampRed, -0.4), 0); // dark red hatch pilot (consequence)
  }

  // podiums: LEAST DISAPPOINTING, under glass
  for (const p of PODIUMS) {
    staticB.boxG(p.x, 0.5, p.z, 0.8, 1.0, 0.8, shade(PAL.bone, -0.06), { band: 1, grad: 0.2 });
    staticB.boxG(p.x, 0.05, p.z, 0.9, 0.1, 0.9, shade(PAL.bone, -0.22), { grad: 0.4 }); // base
    staticB.boxG(p.x, 1.04, p.z, 0.9, 0.08, 0.9, PAL.brass, { grad: 0.12 });
    // proud 4-sided brass bezel holding the frost
    for (const [ox, oz, sx, sz] of [[0, 0.44, 0.86, 0.05], [0, -0.44, 0.86, 0.05], [-0.43, 0, 0.05, 0.9], [0.43, 0, 0.05, 0.9]] as [number, number, number, number][]) {
      staticB.box(p.x + ox, 1.35, p.z + oz, sx, 0.52, sz, shade(PAL.brass, 0.05), 0);
    }
    frostQuads.wallQuad(p.x, 1.35, p.z + 0.44, 0.82, 0.48, 0, FULL_UV);
  }

  // archive drawer wall: 10 x 6 manila drawers + brass pulls
  const dW = (DRAWERS.x1 - DRAWERS.x0) / DRAWERS.cols;
  for (let col = 0; col < DRAWERS.cols; col++) {
    for (let row = 0; row < DRAWERS.rows; row++) {
      const x = DRAWERS.x0 + dW * (col + 0.5);
      const y = DRAWERS.y0 + row * DRAWERS.rowH;
      const proud = (col * DRAWERS.rows + row) % 7 === 3 ? 0.05 : 0; // every 7th ajar
      if (proud > 0) staticB.box(x, y, DRAWERS.z + 0.3, dW - 0.14, DRAWERS.rowH - 0.1, 0.05, shade(PAL.carbon, -0.03), 0); // dark interior lip
      staticB.boxG(x, y, DRAWERS.z + 0.28 + proud, dW - 0.12, DRAWERS.rowH - 0.08, 0.5, PAL.manila, { grad: 0.14 });
      staticB.box(x, y - 0.02, DRAWERS.z + 0.55 + proud, 0.3, 0.045, 0.045, PAL.brass, 0);
      staticB.box(x, y + 0.14, DRAWERS.z + 0.545 + proud, 0.36, 0.12, 0.02, shade(PAL.paper, -0.06), 0);
    }
  }

  // registry of the purged: struck-through brass plaques, hung by hands and not
  // machines — each sits at a seeded 1-2 degree tilt about the wall normal; the
  // strike bar overhangs both plaque edges by 2cm; the brass lifts a touch to
  // catch the registry's single pool (the room's one glint). The final slot is
  // an EMPTY MOUNT — four bolt holes waiting for the next name to be struck.
  {
    const span = REGISTRY_PLAQUES.x1 - REGISTRY_PLAQUES.x0;
    const rz = REGISTRY_PLAQUES.z;
    const glint = shade(PAL.brass, 0.05);
    const total = REGISTRY_PLAQUES.rows.length * REGISTRY_PLAQUES.perRow;
    let n = 0;
    for (let r = 0; r < REGISTRY_PLAQUES.rows.length; r++) {
      for (let i = 0; i < REGISTRY_PLAQUES.perRow; i++, n++) {
        const x = REGISTRY_PLAQUES.x0 + (span / REGISTRY_PLAQUES.perRow) * (i + 0.5);
        const y = REGISTRY_PLAQUES.rows[r];
        // deterministic per-slot jitter (identical for every player; no Math.random)
        const hh = Math.sin((n + 1) * 12.9898) * 43758.5453;
        const a = ((hh - Math.floor(hh)) * 2 - 1) * 0.035; // ~+/-2 degrees, about +z
        if (n === total - 1) {
          // the expected vacancy: a faded mount outline + four dark bolt holes
          staticB.box(x, y, rz + 0.02, 0.85, 0.28, 0.02, shade(PAL.bone, -0.28), 0);
          for (const ox of [-0.34, 0.34]) for (const oy of [-0.09, 0.09]) {
            staticB.box(x + ox, y + oy, rz + 0.035, 0.05, 0.05, 0.03, shade(PAL.carbon, -0.08), 0);
          }
          continue;
        }
        // plaque body, tilted about the wall normal (Z)
        _m4.makeRotationZ(a).setPosition(x, y, rz + 0.03);
        staticB.absorb(new BoxGeometry(0.85, 0.28, 0.04), _m4.clone(), glint, 0);
        // the strike: overhangs the plaque by 2cm each side, tilted to match
        _m4.makeRotationZ(a).setPosition(x, y, rz + 0.06);
        staticB.absorb(new BoxGeometry(0.89, 0.04, 0.02), _m4.clone(), PAL.carbon, 0);
      }
    }
  }

  // appeals office dressing: desk + a real banker's lamp (base, stem, arm,
  // shade with a warm underside) — the one warm practical in the building.
  // v3-2: the wood desk was flat `box` — the last hero furniture not on the
  // top-lit gradient; convert to boxG with the appeals brown-black shadow and a
  // generous warm rim (this is the warmest interior — the softness must land).
  const ash = roomMoodAt(-21, 13.5).shadow; // appeals warm brown-black
  staticB.boxG(-21, 0.72, 13.5, 1.8, 0.08, 0.9, PAL.wood, { grad: 0.08, dark: ash, topLift: 0.8 });
  staticB.boxG(-21.6, 0.36, 13.5, 0.1, 0.72, 0.8, shade(PAL.wood, -0.2), { grad: 0.2, dark: ash });
  staticB.boxG(-20.4, 0.36, 13.5, 0.1, 0.72, 0.8, shade(PAL.wood, -0.2), { grad: 0.2, dark: ash });
  {
    const LX = -21.45, LZ = 13.2, TOP = 0.76;
    _m4.identity().setPosition(LX, TOP + 0.02, LZ);
    staticB.absorb(new CylinderGeometry(0.06, 0.085, 0.04, 10), _m4.clone(), shade(PAL.carbon, 0.15), 0);
    _m4.identity().setPosition(LX, TOP + 0.15, LZ);
    staticB.absorb(new CylinderGeometry(0.012, 0.012, 0.24, 8), _m4.clone(), PAL.brass, 0);
    staticB.box(LX + 0.06, TOP + 0.275, LZ + 0.04, 0.14, 0.02, 0.02, PAL.brass, 0); // arm
    staticB.box(LX + 0.13, TOP + 0.3, LZ + 0.07, 0.3, 0.09, 0.18, shade(PAL.gold, -0.3), 0); // shade
    // v3-2: the one warm practical — the green shade's underlight reads gold-GREEN,
    // gas warmed toward the appeals tungsten wash (§4.2 lever 3), never white.
    unlitB.box(LX + 0.13, TOP + 0.252, LZ + 0.07, 0.26, 0.014, 0.15, mix(PAL.gas, roomWash('appealsOffice'), 0.5), 0); // lit underside
    staticB.box(LX + 0.2, TOP + 0.2, LZ + 0.12, 0.008, 0.07, 0.008, PAL.carbon, 0); // pull chain
  }
  // the lamp's pool lives ON the desk surface — paint-height, never floating
  glowB.floorQuad(-21.15, 13.4, 1.0, 0.62, 0, FULL_UV, 0.775);

  // ---- locked doors (decoration on solid walls) ---------------------------
  for (const d of DOORS) {
    const a = (d.rot * Math.PI) / 180;
    const nx = Math.sin(a), nz = Math.cos(a); // outward normal
    const px = d.x + nx * 0.02, pz = d.z + nz * 0.02;
    staticB.boxG(px, d.h / 2, pz, d.rot === 0 ? d.w : 0.16, d.h, d.rot === 0 ? 0.16 : d.w, shade(PAL.carbon, d.id === 'dept6' ? 0.02 : 0.12), { grad: 0.22 });
    // kick plate: brass, bottom 20cm, proud of the slab (dept6 gets nothing)
    if (d.id !== 'dept6') {
      staticB.box(px + nx * 0.03, 0.12, pz + nz * 0.03,
        d.rot === 0 ? d.w - 0.08 : 0.06, 0.2, d.rot === 0 ? 0.06 : d.w - 0.08, PAL.brass, 0);
    }
    // door-caste status pilot: mezzanine amber-off, others none
    if (d.id === 'mezzanine') {
      unlitB.box(px + nx * 0.05, d.h * 0.62, pz + nz * 0.05 + (d.rot === 0 ? 0 : 0), 0.05, 0.06, 0.05, shade(PAL.amber, -0.55), 0);
    }
    // door frame: jambs + lintel proud of the wall (reads at arm's length)
    const jx = d.rot === 0 ? (d.w / 2 + 0.06) : 0;
    const jz = d.rot === 0 ? 0 : (d.w / 2 + 0.06);
    for (const sgn of [-1, 1]) {
      staticB.boxG(px + sgn * jx + nx * 0.02, d.h / 2 + 0.05, pz + sgn * jz + nz * 0.02,
        d.rot === 0 ? 0.1 : 0.2, d.h + 0.1, d.rot === 0 ? 0.2 : 0.1, shade(PAL.bone, -0.3), { grad: 0.16 });
    }
    staticB.box(px + nx * 0.02, d.h + 0.1, pz + nz * 0.02,
      d.rot === 0 ? d.w + 0.22 : 0.2, 0.1, d.rot === 0 ? 0.2 : d.w + 0.22, shade(PAL.bone, -0.3), 0);
    // handle: shaft + lever + escutcheon plate
    const hx = px + nx * 0.09 + (d.rot === 0 ? d.w * 0.32 : 0);
    const hz = pz + nz * 0.09 + (d.rot === 0 ? 0 : d.w * 0.32);
    staticB.box(hx, d.h * 0.48, hz, 0.05, 0.14, 0.05, PAL.brass, 0);
    staticB.box(hx - (d.rot === 0 ? 0.06 : 0), d.h * 0.48 + 0.05, hz - (d.rot === 0 ? 0 : 0.06),
      d.rot === 0 ? 0.16 : 0.04, 0.035, d.rot === 0 ? 0.04 : 0.16, PAL.brass, 0);
    staticB.box(hx - nx * 0.045, d.h * 0.48, hz - nz * 0.045,
      d.rot === 0 ? 0.1 : 0.02, 0.3, d.rot === 0 ? 0.02 : 0.1, shade(PAL.brass, -0.25), 0);
  }

  // Dept-6 phosphor leak: the game's only green, under and beside the door
  const d6 = DOORS.find((d) => d.id === 'dept6');
  const dept6GlowMat = new MeshBasicMaterial({
    color: new Color(PAL.phosphor), transparent: true, opacity: 0.8, blending: AdditiveBlending, depthWrite: false, side: DoubleSide,
  });
  const d6Glow = new QuadBatch();
  if (d6) {
    // the game's only green must be unmissable: hot core at the threshold,
    // a wide spill, and bright jamb seams — the promise, kept lit
    d6Glow.floorQuad(d6.x, d6.z + 0.14, d6.w * 0.85, 0.24, 0, FULL_UV, 0.02);
    d6Glow.floorQuad(d6.x, d6.z + 0.5, d6.w + 0.6, 0.95, 0, FULL_UV, 0.015);
    d6Glow.wallQuad(d6.x - d6.w / 2 - 0.04, d6.h / 2, d6.z + 0.1, 0.09, d6.h, 0, FULL_UV);
    d6Glow.wallQuad(d6.x + d6.w / 2 + 0.04, d6.h / 2, d6.z + 0.1, 0.09, d6.h, 0, FULL_UV);
    d6Glow.wallQuad(d6.x, d6.h + 0.06, d6.z + 0.1, d6.w * 0.9, 0.07, 0, FULL_UV);
  }
  // M-C: the Descent stairhead is the one place the building's green is
  // unrestricted above Floor -1 — a phosphor wash RISING out of the shaft mouth
  // (Machine Floor light bleeding up). Same phosphor material, same mesh.
  d6Glow.floorQuad(-22, -21, 2.4, 2.0, 0, FULL_UV, 0.05); // pit glow
  d6Glow.floorQuad(-22, -19.4, 3.4, 1.4, 0, FULL_UV, 0.03); // spill toward the door
  d6Glow.wallQuad(-22, 0.9, -24.85, 3.2, 1.6, 0, FULL_UV); // low wash up the north wall
  const dept6Glow = new Mesh(d6Glow.build(), dept6GlowMat);
  group.add(dept6Glow);

  // ---- decals: crisp wayfinding quads over the floor ----------------------
  const decalAtlas = makeDecalAtlas();
  const decalB = new QuadBatch();
  for (const dec of DECALS) {
    decalB.floorQuad(dec.x, dec.z, dec.sx, dec.sz, dec.rot, decalAtlas.regions[dec.kind]);
  }
  const decalMat = new MeshBasicMaterial({
    map: decalAtlas.texture, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2,
  });
  patchMaterial(decalMat);
  const decalMesh = new Mesh(decalB.build(), decalMat);
  group.add(decalMesh);

  // ---- posters ------------------------------------------------------------
  const posterAtlas = makePosterAtlas();
  const posterB = new QuadBatch();
  POSTER_SPOTS.forEach((p, i) => {
    const a = (p.rot * Math.PI) / 180;
    const nx = Math.sin(a), nz = Math.cos(a);
    const flat = p.rot === 0;
    // 1cm relief backing board behind the poster
    staticB.boxG(p.x - nx * 0.01, p.y, p.z - nz * 0.01, flat ? 1.16 : 0.05, 1.52, flat ? 0.05 : 1.16, shade(PAL.bone, -0.06), { grad: 0.12 });
    posterB.wallQuad(p.x + nx * 0.03, p.y, p.z + nz * 0.03, 1.05, 1.4, p.rot, posterAtlas.cell(i % 12));
    // two brass clip dots at the top corners
    for (const s of [-1, 1]) {
      const cxp = p.x + (flat ? s * 0.45 : 0);
      const czp = p.z + (flat ? 0 : s * 0.45);
      staticB.box(cxp + nx * 0.04, p.y + 0.66, czp + nz * 0.04, flat ? 0.08 : 0.03, 0.06, flat ? 0.03 : 0.08, PAL.brass, 0);
    }
  });
  const posterMat = new MeshBasicMaterial({ map: posterAtlas.texture });
  patchMaterial(posterMat);
  group.add(new Mesh(posterB.build(), posterMat));

  // ---- public record gallery: 64 frames -----------------------------------
  const galleryAtlas = makeGalleryAtlas();
  const galleryB = new QuadBatch();
  const GZ = GALLERY_WALL.z;
  const fw = GALLERY_WALL.frame.w, fh = GALLERY_WALL.frame.h;
  let placed = 0;
  outer: for (const rowY of [1.5, 2.7, 3.9]) {
    for (const seg of GALLERY_WALL.segments) {
      const n = Math.floor((seg.x1 - seg.x0) / GALLERY_WALL.frame.gap);
      for (let i = 0; i < n; i++) {
        if (placed >= GALLERY_WALL.count) break outer;
        const x = seg.x0 + GALLERY_WALL.frame.gap * (i + 0.5);
        // RELIEF: a proud carbon frame box behind the picture, a lit top-edge
        // lip that catches the picture light, and the picture proud of both
        staticB.boxG(x, rowY, GZ + 0.02, fw + 0.08, fh + 0.08, 0.05, shade(PAL.carbon, 0.06), { grad: 0.16 });
        staticB.box(x, rowY + fh / 2 + 0.03, GZ + 0.05, fw + 0.1, 0.02, 0.03, shade(PAL.carbon, 0.28), 0); // lit top lip
        galleryB.wallQuad(x, rowY, GZ + 0.06, fw, fh, 0, galleryAtlas.cell(placed));
        // brass WITNESS button below each frame (2cm)
        staticB.box(x, rowY - fh / 2 - 0.06, GZ + 0.06, 0.05, 0.05, 0.04, PAL.brass, 0);
        placed++;
      }
    }
  }
  // picture lights: two warm emissive bars per gallery segment + wash cards
  for (const seg of GALLERY_WALL.segments) {
    const mid = (seg.x0 + seg.x1) / 2;
    const span = seg.x1 - seg.x0;
    for (const bx of [mid - span * 0.25, mid + span * 0.25]) {
      // v3-2: picture lights read BRASS-warm (gas bent toward the Gallery wash),
      // so the frames glow reverent-warm, not clinical gas-green (§3.2 Gallery).
      unlitB.box(bx, 4.55, GZ - 0.35, span * 0.4, 0.06, 0.12, mix(shade(PAL.gas, 0.08), roomWash('gallery'), 0.55), 0); // bar
      staticB.box(bx, 4.6, GZ - 0.42, span * 0.42, 0.05, 0.16, shade(PAL.carbon, 0.14), 0); // hood
      glowB.wallQuad(bx, 3.5, GZ + 0.09, span * 0.5, 2.4, 0, FULL_UV); // warm wash down the frames
    }
  }
  const galleryMat = new MeshBasicMaterial({ map: galleryAtlas.texture });
  patchMaterial(galleryMat);
  group.add(new Mesh(galleryB.build(), galleryMat));

  // ---- plaque, stencils, letterhead board ---------------------------------
  const plaqueB = new QuadBatch();
  // 3cm relief box under the plaque + 4 hex bolts (faces -x, west into intake)
  staticB.boxG(PLAQUE.x + 0.02, PLAQUE.y, PLAQUE.z, 0.06, 0.63, 1.2, shade(PAL.brass, -0.22), { grad: 0.14 });
  for (const oy of [-0.22, 0.22]) for (const oz of [-0.5, 0.5]) hexBolt(staticB, PLAQUE.x - 0.02, PLAQUE.y + oy, PLAQUE.z + oz, 0.04, 0.06, PAL.brass, '-x');
  plaqueB.wallQuad(PLAQUE.x - 0.04, PLAQUE.y, PLAQUE.z, 1.1, 0.55, PLAQUE.rot, FULL_UV);
  const plaqueMat = new MeshBasicMaterial({ map: makePlaqueTexture() });
  patchMaterial(plaqueMat);
  group.add(new Mesh(plaqueB.build(), plaqueMat));

  for (const d of DOORS) {
    if (!d.stencil) continue;
    const sB = new QuadBatch();
    const a = (d.rot * Math.PI) / 180;
    sB.wallQuad(d.x + Math.sin(a) * 0.12, 1.7, d.z + Math.cos(a) * 0.12, 1.0, 0.25, d.rot, FULL_UV);
    const sMat = new MeshBasicMaterial({ map: makeStencilTexture(d.stencil), transparent: true });
    patchMaterial(sMat);
    group.add(new Mesh(sB.build(), sMat));
  }

  const boardB = new QuadBatch();
  boardB.wallQuad(LETTERHEAD_BOARD.x, 3.4, -5.72, LETTERHEAD_BOARD.w, LETTERHEAD_BOARD.h, 0, FULL_UV);
  const boardMat = new MeshBasicMaterial({ map: makeLetterheadBoardTexture() });
  patchMaterial(boardMat);
  group.add(new Mesh(boardB.build(), boardMat));

  // board needs a backing slab poking above its wall (graded) + cornice lips
  staticB.boxG(LETTERHEAD_BOARD.x, 3.4, -5.9, LETTERHEAD_BOARD.w + 0.3, LETTERHEAD_BOARD.h + 0.3, 0.2, shade(PAL.bone, -0.1), { band: 1, grad: 0.14 });
  for (const oy of [LETTERHEAD_BOARD.h / 2 + 0.16, -LETTERHEAD_BOARD.h / 2 - 0.16]) {
    staticB.box(LETTERHEAD_BOARD.x, 3.4 + oy, -5.78, LETTERHEAD_BOARD.w + 0.5, 0.12, 0.34, shade(PAL.bone, 0.08), 0); // cornice lip
  }
  // two downlight bars above the board — the Daily Directive is the brightest wall
  for (const bx of [LETTERHEAD_BOARD.x - 1.6, LETTERHEAD_BOARD.x + 1.6]) {
    unlitB.box(bx, 3.4 + LETTERHEAD_BOARD.h / 2 - 0.05, -5.62, 1.4, 0.06, 0.14, shade(PAL.gas, 0.06), 0);
    staticB.box(bx, 3.4 + LETTERHEAD_BOARD.h / 2 + 0.02, -5.68, 1.5, 0.06, 0.2, shade(PAL.carbon, 0.12), 0); // hood
    glowB.wallQuad(bx, 3.4 + LETTERHEAD_BOARD.h / 2 - 1.1, -5.5, 1.7, 2.0, 0, FULL_UV);
  }

  // ---- hardware labels: one mesh for every new READ-3 label (+1 draw call) --
  const hwMat = new MeshBasicMaterial({ map: hwAtlas.texture, transparent: true, alphaTest: 0.02 });
  patchMaterial(hwMat);
  const hwMesh = new Mesh(hwB.build(), hwMat);
  hwMesh.matrixAutoUpdate = false;
  group.add(hwMesh);

  // ---- chyron cornices (entities skin them with the live texture) ---------
  // geometry built here; material assigned by props.ts (the ONE chyron texture)
  const chyronB = new QuadBatch();
  for (const ch of CHYRONS) {
    // the live strip sits proud of its housing tray, never inside it
    chyronB.wallQuad(ch.x, ch.y, ch.z + 0.06, ch.len, 0.42, ch.rot, FULL_UV);
    unlitB.box(
      ch.x, ch.y, ch.z - 0.03, ch.rot === 0 ? ch.len + 0.2 : 0.1, 0.52, ch.rot === 0 ? 0.1 : ch.len + 0.2,
      shade(PAL.carbon, 0.04), 0
    );
  }
  const chyronGeo = chyronB.build();

  // ---- THE CEILING (the FP sky; hidden under the long lens) ---------------
  // Tile grid at wall height with a service chase left open along the north
  // wall (the pneumatic trunk runs at 5.5m) and the rotunda open to a high
  // dark plenum — the two-story Vault face keeps its stories. A carbon
  // closure plane at 7.4m seals the void everywhere.
  const ceiling = new Group();
  {
    const CEIL_Y = 4.5;
    const tileTex = makeCeilingTexture();
    const tilePos: number[] = [];
    const tileUv: number[] = [];
    const ceilRect = (x0: number, z0: number, x1: number, z1: number, y: number): void => {
      tilePos.push(x0, y, z0, x1, y, z1, x1, y, z0, x0, y, z0, x0, y, z1, x1, y, z1);
      const u = (x: number): number => x / 2.4;
      const v = (z: number): number => z / 2.4;
      tileUv.push(u(x0), v(z0), u(x1), v(z1), u(x1), v(z0), u(x0), v(z0), u(x0), v(z1), u(x1), v(z1));
    };
    ceilRect(-30, -15.5, 30, -6, CEIL_Y); // window row / archive band
    ceilRect(-12, -6, 30, 6, CEIL_Y); // hall + intake (rotunda excluded)
    ceilRect(-30, 6, 30, 17, CEIL_Y); // south rooms
    ceilRect(-38, -25, 30, -17, CEIL_Y); // M-C: north service ring
    ceilRect(-38, -17, -30, 17, CEIL_Y); // M-C: west service spine
    const tileGeo = new BufferGeometry();
    tileGeo.setAttribute('position', new BufferAttribute(new Float32Array(tilePos), 3));
    tileGeo.setAttribute('uv', new BufferAttribute(new Float32Array(tileUv), 2));
    tileGeo.computeVertexNormals();
    const tileMat = new MeshBasicMaterial({ map: tileTex, side: DoubleSide });
    patchMaterial(tileMat);
    const tileMesh = new Mesh(tileGeo, tileMat);
    tileMesh.matrixAutoUpdate = false;
    ceiling.add(tileMesh);

    // ceiling furniture: troffer surrounds, vents, lamp mounts, chase edging
    const ceilB = new Batch();
    for (const room of ROOMS) {
      for (const [px2, pz2] of room.pools) {
        ceilB.box(px2, 4.46, pz2, 2.72, 0.05, 1.24, shade(PAL.carbon, 0.16), 0); // troffer frame
      }
      // one return-air vent per room, off the room's center
      const vx = (room.x0 + room.x1) / 2 + 1.7;
      const vz = (room.z0 + room.z1) / 2 - 1.2;
      ceilB.box(vx, 4.465, vz, 0.64, 0.05, 0.64, shade(PAL.carbon, 0.12), 0);
      for (let sIdx = 0; sIdx < 4; sIdx++) {
        ceilB.box(vx, 4.44, vz - 0.21 + sIdx * 0.14, 0.56, 0.02, 0.05, shade(PAL.ghost, -0.25), 0);
      }
    }
    for (const [lx, lz] of LAMPS) {
      ceilB.box(lx, 4.47, lz, 0.36, 0.08, 0.36, shade(PAL.carbon, 0.06), 0); // eye-lamp mount plate
    }
    // chase edge trim where the tiles stop short of the north wall
    ceilB.box(0, 4.47, -15.5, 60, 0.1, 0.08, shade(PAL.carbon, 0.1), 0);
    const ceilMat = new MeshLambertMaterial({ vertexColors: true, flatShading: true });
    patchMaterial(ceilMat, { banded: true });
    const ceilMesh = new Mesh(ceilB.build(), ceilMat);
    ceilMesh.matrixAutoUpdate = false;
    ceiling.add(ceilMesh);

    // the plenum: a dark closure plane high above everything
    const voidMat = new MeshBasicMaterial({ color: new Color(shade(PAL.carbon, 0.03)).convertSRGBToLinear(), side: DoubleSide });
    patchMaterial(voidMat);
    const voidMesh = new Mesh(new PlaneGeometry(76, 48), voidMat);
    voidMesh.rotation.x = Math.PI / 2;
    voidMesh.position.set(-4, 7.4, -4);
    voidMesh.matrixAutoUpdate = false;
    voidMesh.updateMatrix();
    ceiling.add(voidMesh);
  }
  ceiling.visible = false; // the long lens never sees it; boot flips it for FP
  group.add(ceiling);

  // ---- finalize batches ---------------------------------------------------
  const staticMat = new MeshLambertMaterial({ vertexColors: true, flatShading: true });
  patchMaterial(staticMat, { banded: true });
  const staticMesh = new Mesh(staticB.build(), staticMat);
  // GRAPHICS R1 (#75): the architecture + furniture cast AND receive. The
  // ceiling (a separate grandchild group) deliberately does NOT cast — a solid
  // overhead caster would shadow the entire floor from the 35deg key.
  staticMesh.castShadow = true;
  staticMesh.receiveShadow = true;
  staticMesh.matrixAutoUpdate = false;
  group.add(staticMesh);

  // clerk paperwork torsos: one mesh per window (windows.ts shuffles them)
  const clerkTorsos: Mesh[] = [];
  for (let i = 0; i < WINDOW_X.length; i++) {
    // each torso gets its OWN material clone (same cached program) so a summons
    // can lift just that clerk's brightness — the window's little theater lights
    const m = new Mesh(clerkGeos[i], staticMat.clone());
    m.position.set(WINDOW_X[i], 0, WINDOW_Z + 0.23); // inside the lightbox bay
    group.add(m);
    clerkTorsos.push(m);
  }

  // GRAPHICS R1 (#75): emissives are LIGHT, not surfaces — exempt them from the
  // scene tonemapping so the gas tubes / amber pilots stay hot and read as bright
  // practicals against the now-tonemapped, higher-contrast room instead of being
  // rolled off toward the mid-tones with everything else.
  const unlitMat = new MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  patchMaterial(unlitMat);
  const unlitMesh = new Mesh(unlitB.build(), unlitMat);
  unlitMesh.matrixAutoUpdate = false;
  group.add(unlitMesh);

  const glowMat = new MeshBasicMaterial({
    color: new Color(PAL.gas), transparent: true, opacity: 0.09, blending: AdditiveBlending, depthWrite: false, side: DoubleSide, toneMapped: false,
  });
  const glowMesh = new Mesh(glowB.build(), glowMat);
  glowMesh.matrixAutoUpdate = false;
  group.add(glowMesh);

  const tris = staticB.tris + unlitB.tris;
  const buildMs = performance.now() - t0;

  return {
    group, tris, buildMs,
    w3Tube, w3Glow, dept6Glow, pilotLamp, beacon, vaultMouthGlow,
    copierPilots, copierScan, turnstileArms, frostMat,
    windowAnchors: WINDOW_X.map((x) => ({ x, z: WINDOW_Z })),
    chyronGeo,
    ceiling, clerkTorsos, glowMat,
  };
}

const FULL_UV = { u0: 0, v0: 0, u1: 1, v1: 1 };
